import store from '@store'

/**
 * Builds default request headers and optionally filters them.
 *
 * @param {string[]} [filter=[]] - Header keys to keep in the returned object.
 * @returns {Record<string, string>} Request headers with optional filtering.
 */
export function headers (filter = []) {
  const { token } = store.state.auth

  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  }

  return !filter.length ? headers :
    filter.reduce((acc, key) => ({
      ...acc,
      [key]: headers[key]
    }), {})
}

/**
 * Resolves the file extension from a base64 data URI signature.
 *
 * @param {string} encoded - Full encoded base64 data URI.
 * @returns {string} File extension inferred from base64 content.
 */
const base64Extension = (encoded) => {
  const EXTENSIONS = {
    '/': 'jpg',
    'i': 'png',
    'R': 'gif',
    'U': 'webp',
    'J': 'pdf',
    'P': 'svg',
  }

  const [ , content = '' ] = encoded.split(';base64,') || []

  return EXTENSIONS[content.charAt(0)] || 'png'
}

/**
 * Converts a data URI into a File object.
 *
 * @param {string} dataURI - Data URI in base64 or URL-encoded format.
 * @returns {File} File created from the provided data URI.
 */
export function dataURItoBlob (dataURI) {
  // convert base64/URLEncoded data component to raw binary data held in a string
  let byteString;
  if (dataURI.split(',')[0].indexOf('base64') >= 0)
    byteString = atob(dataURI.split(',')[1]);
  else
    byteString = unescape(dataURI.split(',')[1]);

  // separate out the mime component
  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

  // write the bytes of the string to a typed array
  const ia = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  const ext = base64Extension(dataURI)

  return new File([ ia ], `photo.${ext}`, { type: mimeString });
}

const BASE_URL = import.meta.env.VITE_MIDDLEWARE_URL

/**
 * Checks whether a response-like object has JSON content type.
 *
 * @param {{ headers? }} response - Response-like object.
 * @returns {boolean} True when content type includes application/json.
 */
const isJsonContent = (response = {}) => {
  const contentType = response.headers?.get('content-type') || ''

  return contentType.includes('application/json')
}

/**
 * Executes an HTTP request against middleware API.
 *
 * @param {string} url - Relative endpoint path.
 * @param {Object} options - Fetch options.
 * @returns {Promise<Object>} Parsed JSON response or response body stream.
 */
const request = async (url, options = {}) => {
  try {
    const response = await fetch(BASE_URL + url, {
      ...options,
      headers: {
        ...options.headers || {},
        ...headers([ 'Authorization', 'Accept', 'Content-Type' ]),
      },
    })

    if (!response.ok)
      throw response

    if (isJsonContent(response))
      return response.json()

    return response.body
  } catch (err) {
    if (isJsonContent(err)) {
      const { errors } = await err.json()

      if (Array.isArray(errors))
        throw errors

      throw Object.values(errors)
    }

    throw err
  }
}

/**
 * Appends query params to a URL, removing empty values.
 *
 * @param {string} url - Base relative URL.
 * @param {Object} params - Query params object.
 * @returns {string} URL with serialized query string.
 */
const getUrlParams = (url, params = {}) => {
  const query = new URLSearchParams()

  Object.entries(params)
    .filter(([ , value ]) => value !== undefined && value !== null && value !== '')
    .forEach(([ key, value ]) =>
      query.set(key, value)
    )

  const queryString = query.toString() ? `?${query}` : ''

  return url + queryString
}

export default {
  get: (url, options = {}) => request(
    getUrlParams(url, options.params),
    { ...options, method: 'GET' }
  ),

  post: (url, options = {}) => request(
    getUrlParams(url, options.params),
    { ...options, method: 'POST', body: JSON.stringify(options.body || {}) }
  ),

  put: (url, options = {}) => request(
    getUrlParams(url, options.params),
    { ...options, method: 'PUT', body: JSON.stringify(options.body || {}) }
  ),

  delete: (url, options = {}) => request(
    getUrlParams(url, options.params),
    { ...options, method: 'DELETE' }
  ),
}
